import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import DecimalField from '../shared/DecimalField';
import { formatPrice, calcPriceExclVat, calcPriceInclVat, DEFAULT_VAT_RATE } from '../../utils/vat';
import VatModeToggle from '../shared/VatModeToggle';
import VatConverseLine from '../shared/VatConverseLine';

const findNewPartIds = (oldParts, newParts) => {
  const oldIds = new Set((oldParts || []).map((p) => p.id));
  return (newParts || []).filter((p) => !oldIds.has(p.id)).map((p) => p.id);
};

/**
 * Vendor catalogue punch-out + basket import for one target: a work order
 * (`maintenance` + `setMaintenance`, the default) or a lähete (`partsSale` +
 * `onImported(count)`): the basket lands as to_order special-order lines there.
 */
function SupplierPartsTab({
  maintenance,
  partsSale,
  awaitingConfirmation,
  hideFromRegistry,
  setMaintenance,
  onPartsImported,
  onImported,
  onError,
  handleClose,
}) {
  const isPartsSale = !!partsSale;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [integrations, setIntegrations] = useState([]);
  const [integrationsLoading, setIntegrationsLoading] = useState(true);

  // Per-integration cart state, keyed by integration.id
  const [cartState, setCartState] = useState({});

  const CART_DEFAULTS = {
    items: [],
    loading: false,
    fetched: false,
    saving: false,
    expanded: {},
    catalogLoading: false,
    error: null,
  };

  const updateCartState = useCallback((integrationId, updates) => {
    setCartState((prev) => ({
      ...prev,
      [integrationId]: { ...CART_DEFAULTS, ...prev[integrationId], ...updates },
    }));
  }, []);

  const getCart = useCallback(
    (integrationId) => ({
      ...CART_DEFAULTS,
      ...cartState[integrationId],
    }),
    [cartState]
  );

  // Fetch integrations on mount
  useEffect(() => {
    const fetchIntegrations = async () => {
      try {
        const response = await axios.get('/api/suppliers/');
        // Email-ordered vendors have no web catalogue or cart to import from.
        setIntegrations((response.data.results || []).filter((i) => i.supplier_type !== 'email'));
      } catch (err) {
        console.error('Failed to fetch supplier integrations:', err);
      } finally {
        setIntegrationsLoading(false);
      }
    };
    fetchIntegrations();
  }, []);

  const openCatalog = async (integrationId) => {
    updateCartState(integrationId, { catalogLoading: true });
    try {
      const params = {};
      const reg = maintenance?.vehicle?.registration_number;
      if (reg) {
        params.registration_number = reg;
      }
      const response = await axios.get(`/api/suppliers/${integrationId}/catalog-url/`, { params });
      window.open(response.data.url, '_blank');
    } catch (err) {
      onError(t('supplierParts.catalogUrlFailed'));
    } finally {
      updateCartState(integrationId, { catalogLoading: false });
    }
  };

  const fetchCart = async (integrationId) => {
    updateCartState(integrationId, { loading: true, error: null });
    try {
      const response = await axios.get(`/api/suppliers/${integrationId}/cart/`);
      const integration = integrations.find((i) => i.id === integrationId);
      // Atoy's OVH and RatParts' Ovh (srp_price) are VAT-inclusive — keep the raw
      // value and flip the VAT picker to "included" so the user sees the same
      // number as in Osalinkki / the RatParts webshop. Örum keeps its blank default.
      const prefillsGrossSrp = ['atoy', 'ratparts'].includes(integration?.supplier_type);
      const items = (response.data || []).map((item) => ({
        ...item,
        // Use enriched name if available
        product_name: item.enriched_name || item.product_name,
        sell_price: prefillsGrossSrp && item.srp_price != null ? parseFloat(item.srp_price) : null,
        sell_price_includes_vat: prefillsGrossSrp,
        // Meko prices are VAT-inclusive from the API
        price_includes_vat: item.price_includes_vat || false,
        // Manufacturer from enrichment (read-only display)
        manufacturer_name: item.brand || '',
      }));
      updateCartState(integrationId, {
        items,
        loading: false,
        fetched: true,
      });
    } catch (err) {
      updateCartState(integrationId, {
        loading: false,
        error: t('supplierParts.importFailed'),
      });
    }
  };

  const handleCartItemEdit = (integrationId, itemIndex, field, value) => {
    setCartState((prev) => {
      const cart = prev[integrationId];
      if (!cart) return prev;
      const newItems = [...cart.items];
      newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
      return { ...prev, [integrationId]: { ...cart, items: newItems } };
    });
  };

  const handleCartItemVatToggle = (integrationId, itemIndex, field = 'line_price') => {
    setCartState((prev) => {
      const cart = prev[integrationId];
      if (!cart) return prev;
      const item = cart.items[itemIndex];
      const isPrice = field === 'line_price';
      const flagKey = isPrice ? 'price_includes_vat' : 'sell_price_includes_vat';
      const wasIncl = item[flagKey];
      const currentPrice = parseFloat(item[field]) || 0;
      const newPrice = wasIncl
        ? calcPriceExclVat(currentPrice, DEFAULT_VAT_RATE)
        : calcPriceInclVat(currentPrice, DEFAULT_VAT_RATE);
      const newItems = [...cart.items];
      newItems[itemIndex] = {
        ...item,
        [flagKey]: !wasIncl,
        [field]: Math.round(newPrice * 100) / 100,
      };
      return { ...prev, [integrationId]: { ...cart, items: newItems } };
    });
  };

  const handleCartItemRemove = (integrationId, itemIndex) => {
    setCartState((prev) => {
      const cart = prev[integrationId];
      if (!cart) return prev;
      const newItems = cart.items.filter((_, i) => i !== itemIndex);
      return { ...prev, [integrationId]: { ...cart, items: newItems } };
    });
  };

  const handleCartItemExpandToggle = (integrationId, itemIndex) => {
    setCartState((prev) => {
      const cart = prev[integrationId];
      if (!cart) return prev;
      const newExpanded = { ...cart.expanded, [itemIndex]: !cart.expanded[itemIndex] };
      return { ...prev, [integrationId]: { ...cart, expanded: newExpanded } };
    });
  };

  const saveCart = async (integrationId) => {
    const cart = getCart(integrationId);
    if (!cart.items.length) return;

    updateCartState(integrationId, { saving: true });
    const oldParts = maintenance?.parts;

    try {
      const payload = {
        ...(isPartsSale ? { parts_sale_id: partsSale.id } : { maintenance_id: maintenance.id }),
        items: cart.items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          line_price: item.line_price,
          price_includes_vat: item.price_includes_vat || false,
          sell_price: item.sell_price && Number.isFinite(parseFloat(item.sell_price))
            ? (item.sell_price_includes_vat
              ? calcPriceExclVat(item.sell_price, DEFAULT_VAT_RATE)
              : parseFloat(item.sell_price))
            : null,
          manufacturer_name: item.manufacturer_name || '',
          ean_code: item.ean_code || '',
        })),
        ...(isPartsSale ? {} : { awaiting_confirmation: awaitingConfirmation }),
        skip_registry: hideFromRegistry,
      };

      const importResponse = await axios.post(
        `/api/suppliers/${integrationId}/cart/import/`,
        payload
      );

      // Clear cart items
      updateCartState(integrationId, {
        items: [],
        fetched: false,
        saving: false,
      });

      if (isPartsSale) {
        if (onImported) onImported(importResponse.data.parts_count);
        return;
      }

      // Refresh maintenance to get updated parts
      const maintenanceRes = await axios.get(`/api/maintenances/${maintenance.id}/`);
      setMaintenance(maintenanceRes.data);

      const newIds = findNewPartIds(oldParts, maintenanceRes.data.parts);
      onPartsImported(importResponse.data.parts_count, newIds);
    } catch (err) {
      updateCartState(integrationId, { saving: false });
      onError(t('supplierParts.importFailed'));
    }
  };

  // Loading state
  if (integrationsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Empty state — no suppliers connected
  if (integrations.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <LocalShippingIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 500, mb: 0.5 }}>
          {t('supplierParts.noSuppliers')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
          {t('supplierParts.noSuppliersDescription')}
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            handleClose();
            navigate('/settings?tab=integrations');
          }}
          sx={{ borderRadius: '10px' }}
        >
          {t('supplierParts.goToSettings')}
        </Button>
      </Box>
    );
  }

  // Supplier cards
  return (
    <Box>
      {integrations.map((integration) => {
        const cart = getCart(integration.id);
        const items = cart.items;

        return (
          <Paper
            key={integration.id}
            elevation={0}
            sx={{
              mb: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: '12px',
              overflow: 'hidden',
            }}
          >
            {/* Card header */}
            <Box sx={{ px: 2, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    flexShrink: 0,
                  }}
                >
                  <LocalShippingIcon fontSize="small" />
                </Box>
                <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                  {integration.display_name}
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('supplierParts.catalogDescription', { name: integration.display_name })}
              </Typography>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    cart.catalogLoading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <OpenInNewIcon fontSize="small" />
                    )
                  }
                  onClick={() => openCatalog(integration.id)}
                  disabled={cart.catalogLoading}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  {t('supplierParts.openCatalog')}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={
                    cart.loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      <ShoppingCartIcon fontSize="small" />
                    )
                  }
                  onClick={() => fetchCart(integration.id)}
                  disabled={cart.loading}
                  data-testid={`supplier-cart-fetch-${integration.id}`}
                  sx={{ borderRadius: '8px', textTransform: 'none' }}
                >
                  {t('supplierParts.importCart')}
                </Button>
              </Box>
            </Box>

            {/* Cart loading state */}
            {cart.loading && (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 3, borderTop: 1, borderColor: 'divider' }}>
                <CircularProgress size={20} />
                <Typography variant="body2" color="text.secondary">
                  {t('supplierParts.fetchingCart')}
                </Typography>
              </Box>
            )}

            {/* Cart error */}
            {cart.error && (
              <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" color="error">
                  {cart.error}
                </Typography>
              </Box>
            )}

            {/* Empty cart */}
            {cart.fetched && !cart.loading && items.length === 0 && (
              <Box sx={{ px: 2, py: 3, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {t('supplierParts.emptyCart')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('supplierParts.emptyCartDescription')}
                </Typography>
              </Box>
            )}

            {/* Cart items */}
            {items.length > 0 && (
              <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
                {/* Cart count header */}
                <Box sx={{ px: 2, py: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    {t('supplierParts.partsInCart', { count: items.length })}
                  </Typography>
                </Box>

                {/* Item cards */}
                {items.map((item, itemIndex) => (
                  <Box
                    key={itemIndex}
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      '&:last-child': { borderBottom: 0 },
                    }}
                  >
                    {/* Desktop layout */}
                    <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', px: 2, py: 1.5, gap: 1.5 }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography sx={{ fontWeight: 500, fontSize: '14px' }} noWrap>
                            {item.product_name}
                          </Typography>
                          <Tooltip title={t('supplierParts.supplierData')}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'success.main',
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        </Box>
                        {(item.manufacturer_name || item.product_id) && (
                          <Typography variant="caption" color="text.secondary">
                            {item.manufacturer_name && item.product_id
                              ? `${item.manufacturer_name} · ${item.product_id}`
                              : item.product_id}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {item.quantity} {t('pcs')}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                            {formatPrice(item.price_includes_vat ? item.line_price : calcPriceInclVat(item.line_price, DEFAULT_VAT_RATE))} €
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('incl. VAT')}
                          </Typography>
                        </Box>
                        {parseFloat(item.quantity) > 1 && (
                          <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {t('supplierParts.lineTotal')} {formatPrice(item.price_includes_vat
                              ? item.line_price * parseFloat(item.quantity)
                              : calcPriceInclVat(item.line_total || (item.line_price * parseFloat(item.quantity)), DEFAULT_VAT_RATE))} €
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton size="small" onClick={() => handleCartItemExpandToggle(integration.id, itemIndex)}>
                          {cart.expanded[itemIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                        <IconButton size="small" onClick={() => handleCartItemRemove(integration.id, itemIndex)} sx={{ color: 'error.main' }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Mobile layout */}
                    <Box sx={{ display: { xs: 'block', sm: 'none' }, px: 2, py: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                              {item.product_name}
                            </Typography>
                            <Tooltip title={t('supplierParts.supplierData')}>
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  bgcolor: 'success.main',
                                  flexShrink: 0,
                                }}
                              />
                            </Tooltip>
                          </Box>
                          {(item.manufacturer_name || item.product_id) && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {item.manufacturer_name && item.product_id
                                ? `${item.manufacturer_name} · ${item.product_id}`
                                : item.product_id}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                          <IconButton size="small" onClick={() => handleCartItemExpandToggle(integration.id, itemIndex)}>
                            {cart.expanded[itemIndex] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                          <IconButton size="small" onClick={() => handleCartItemRemove(integration.id, itemIndex)} sx={{ color: 'error.main' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      <Box sx={{ mt: 0.75 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            {item.quantity} {t('pcs')}
                          </Typography>
                          <Typography sx={{ fontWeight: 600, fontSize: '14px' }}>
                            {formatPrice(item.price_includes_vat ? item.line_price : calcPriceInclVat(item.line_price, DEFAULT_VAT_RATE))} €
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {t('incl. VAT')}
                          </Typography>
                        </Box>
                        {parseFloat(item.quantity) > 1 && (
                          <Typography sx={{ fontWeight: 600, fontSize: '13px' }}>
                            {t('supplierParts.lineTotal')} {formatPrice(item.price_includes_vat
                              ? item.line_price * parseFloat(item.quantity)
                              : calcPriceInclVat(item.line_total || (item.line_price * parseFloat(item.quantity)), DEFAULT_VAT_RATE))} €
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Expanded edit view */}
                    <Collapse in={cart.expanded[itemIndex]}>
                      <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          <TextField
                            label={t('Name')}
                            size="small"
                            fullWidth
                            value={item.product_name}
                            onChange={(e) => handleCartItemEdit(integration.id, itemIndex, 'product_name', e.target.value)}
                          />
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: 1, minWidth: '200px' }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('Purchase price')}
                              </Typography>
                              <Typography sx={{ fontWeight: 500, fontSize: '14px' }}>
                                {item.price_includes_vat ? (
                                  <>
                                    {formatPrice(calcPriceExclVat(item.line_price, DEFAULT_VAT_RATE))} € {t('excl. VAT')}
                                    {' / '}
                                    {formatPrice(item.line_price)} € {t('incl. VAT')}
                                  </>
                                ) : (
                                  <>
                                    {formatPrice(item.line_price)} € {t('excl. VAT')}
                                    {' / '}
                                    {formatPrice(calcPriceInclVat(item.line_price, DEFAULT_VAT_RATE))} € {t('incl. VAT')}
                                  </>
                                )}
                              </Typography>
                            </Box>
                            <DecimalField
                              label={t('Quantity')}
                              size="small"
                              value={item.quantity ?? ''}
                              onChange={(value) => handleCartItemEdit(integration.id, itemIndex, 'quantity', value)}
                              placeholder="1"
                              sx={{ width: '100px', flexShrink: 0 }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DecimalField
                              label={item.sell_price_includes_vat ? t('Sell price (incl. VAT)') : t('Sell price (excl. VAT)')}
                              size="small"
                              value={item.sell_price ?? ''}
                              onChange={(value) => handleCartItemEdit(integration.id, itemIndex, 'sell_price', value)}
                              allowNegative
                              sx={{ flex: 1 }}
                            />
                            <VatModeToggle
                              value={item.sell_price_includes_vat ? 'incl' : 'excl'}
                              onChange={(e, val) => val && handleCartItemVatToggle(integration.id, itemIndex, 'sell_price')}
                              sx={{ flexShrink: 0 }}
                              help={false}
                            />
                          </Box>
                          <VatConverseLine
                            value={item.sell_price}
                            mode={item.sell_price_includes_vat ? 'incl' : 'excl'}
                            vatRate={DEFAULT_VAT_RATE}
                            sx={{ mt: -1 }}
                          />
                          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1.5 }}>
                            <TextField
                              label={t('Manufacturer')}
                              size="small"
                              fullWidth
                              value={item.manufacturer_name || ''}
                              disabled
                            />
                            <TextField
                              label={t('Code')}
                              size="small"
                              fullWidth
                              value={item.product_id || ''}
                              disabled
                            />
                          </Box>
                        </Box>
                      </Box>
                    </Collapse>
                  </Box>
                ))}

                {/* Save button */}
                <Box sx={{ p: 2, bgcolor: 'background.paper' }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => saveCart(integration.id)}
                    disabled={cart.saving || items.length === 0}
                    data-testid={`supplier-cart-save-${integration.id}`}
                    startIcon={cart.saving ? <CircularProgress size={18} /> : <CheckCircleIcon />}
                    sx={{
                      borderRadius: '10px',
                      bgcolor: 'success.main',
                      '&:hover': { bgcolor: 'success.dark' },
                    }}
                  >
                    {cart.saving
                      ? t('Saving...')
                      : `${t('Save')} ${items.length} ${items.length === 1 ? t('part') : t('parts')}`}
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>
        );
      })}
    </Box>
  );
}

export default SupplierPartsTab;
